import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Save, Palette, Image as ImageIcon, Type, MessageSquare, Wallet, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { useSettings } from '../../contexts/SettingsContext';
import ImageUpload from './ImageUpload';
import TasbihIcon, { TASBIH_ICON_OPTIONS } from '../TasbihIcon';

const FONT_OPTIONS = [
  { id: 'Poppins', label: 'Poppins' },
  { id: 'Outfit', label: 'Outfit' },
  { id: 'Playfair Display', label: 'Playfair Display' },
];

const Section = ({ icon: Icon, title, subtitle, children }) => (
  <div className="bg-white rounded-2xl border border-stone-200 p-6">
    <div className="flex items-center gap-3 mb-5">
      <div className="w-11 h-11 rounded-full bg-emerald-50 flex items-center justify-center">
        <Icon className="w-6 h-6 text-emerald-700" />
      </div>
      <div>
        <h3 className="text-lg font-heading font-bold text-primary-dark">{title}</h3>
        <p className="text-sm font-body text-text-secondary">{subtitle}</p>
      </div>
    </div>
    {children}
  </div>
);

const AppearanceManager = () => {
  const { settings, updateSettings } = useSettings();
  const [form, setForm] = useState({
    hero_image_url: settings.hero_image_url || '',
    greeting_text: settings.greeting_text || '',
    spirit_kit_title: settings.spirit_kit_title || 'Spiritual Kit',
    show_financial_widget: !!settings.show_financial_widget,
    widget_title: settings.widget_title || 'Transparansi Keuangan',
    app_font: settings.app_font || 'Outfit',
    tasbih_icon: settings.tasbih_icon || 'classic',
    pwa_logo_url: settings.pwa_logo_url || '',
    show_download_widget: settings.show_download_widget !== false,
    download_widget_title: settings.download_widget_title || 'Pasang Aplikasi Masjid',
  });
  const [saving, setSaving] = useState(false);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSettings(form);
      toast.success('Pengaturan tampilan berhasil disimpan!');
    } catch (e) {
      toast.error('Gagal menyimpan pengaturan');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full px-4 py-3 rounded-xl border border-stone-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none font-body';

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      {/* Hero Background */}
      <Section icon={ImageIcon} title="Background Hero" subtitle="Gambar latar bagian atas halaman utama">
        <ImageUpload
          value={form.hero_image_url}
          onChange={(url) => set({ hero_image_url: url })}
          label="Upload Background Hero"
        />
      </Section>

      {/* Texts */}
      <Section icon={MessageSquare} title="Teks & Salam" subtitle="Sapaan dan judul bagian beranda">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-body font-medium text-text-primary mb-2">Teks Salam (Greeting)</label>
            <input
              type="text"
              data-testid="appearance-greeting-input"
              value={form.greeting_text}
              onChange={(e) => set({ greeting_text: e.target.value })}
              className={inputCls}
              placeholder="Kosongkan untuk otomatis (Selamat Pagi/Siang/Sore/Malam)"
            />
          </div>
          <div>
            <label className="block text-sm font-body font-medium text-text-primary mb-2">Judul Spirit Kit</label>
            <input
              type="text"
              data-testid="appearance-spiritkit-input"
              value={form.spirit_kit_title}
              onChange={(e) => set({ spirit_kit_title: e.target.value })}
              className={inputCls}
              placeholder="Spiritual Kit"
            />
          </div>
        </div>
      </Section>

      {/* Financial Transparency Toggle */}
      <Section icon={Wallet} title="Transparansi Keuangan" subtitle="Tampilkan widget kas di halaman utama">
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-stone-50 rounded-xl">
            <span className="text-sm font-body font-medium text-text-primary">Tampilkan Widget</span>
            <button
              data-testid="appearance-financial-toggle"
              onClick={() => set({ show_financial_widget: !form.show_financial_widget })}
              className={`relative w-14 h-7 rounded-full transition-colors ${form.show_financial_widget ? 'bg-emerald-600' : 'bg-stone-300'}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform ${form.show_financial_widget ? 'translate-x-7' : ''}`} />
            </button>
          </div>
          <div>
            <label className="block text-sm font-body font-medium text-text-primary mb-2">Judul Widget Keuangan</label>
            <input
              type="text"
              data-testid="appearance-widget-title-input"
              value={form.widget_title}
              onChange={(e) => set({ widget_title: e.target.value })}
              className={inputCls}
              placeholder="Transparansi Keuangan"
            />
          </div>
        </div>
      </Section>

      {/* Font */}
      <Section icon={Type} title="Font Aplikasi" subtitle="Ubah jenis huruf untuk seluruh aplikasi">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {FONT_OPTIONS.map((f) => (
            <button
              key={f.id}
              data-testid={`appearance-font-${f.id.replace(/\s+/g, '-').toLowerCase()}`}
              onClick={() => set({ app_font: f.id })}
              style={{ fontFamily: `'${f.id}'` }}
              className={`px-4 py-4 rounded-xl border text-center transition-colors ${
                form.app_font === f.id ? 'border-emerald-600 bg-emerald-50 ring-2 ring-emerald-200' : 'border-stone-200 hover:border-emerald-300'
              }`}
            >
              <span className="block text-lg font-semibold text-text-primary">Aa</span>
              <span className="block text-sm text-text-secondary mt-1">{f.label}</span>
            </button>
          ))}
        </div>
      </Section>

      {/* Tasbih Icon */}
      <Section icon={Palette} title="Ikon Tasbih" subtitle="Pilih ikon untuk tombol Tasbih di navigasi">
        <div className="grid grid-cols-3 gap-3">
          {TASBIH_ICON_OPTIONS.map((opt) => (
            <button
              key={opt.id}
              data-testid={`appearance-tasbih-icon-${opt.id}`}
              onClick={() => set({ tasbih_icon: opt.id })}
              className={`flex flex-col items-center gap-2 px-3 py-4 rounded-xl border transition-colors ${
                form.tasbih_icon === opt.id ? 'border-emerald-600 bg-emerald-50 ring-2 ring-emerald-200' : 'border-stone-200 hover:border-emerald-300'
              }`}
            >
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(145deg, #10B981, #065F46)' }}>
                <TasbihIcon variant={opt.id} className="w-7 h-7 text-white" />
              </div>
              <span className="text-xs font-body text-text-secondary">{opt.label}</span>
            </button>
          ))}
        </div>
      </Section>

      {/* PWA & Download */}
      <Section icon={Smartphone} title="PWA & Download App" subtitle="Logo aplikasi dan widget ajakan pasang aplikasi">
        <div className="space-y-4">
          <ImageUpload
            value={form.pwa_logo_url}
            onChange={(url) => set({ pwa_logo_url: url })}
            label="Logo PWA / Ikon Aplikasi"
          />
          <div className="flex items-center justify-between p-4 bg-stone-50 rounded-xl">
            <span className="text-sm font-body font-medium text-text-primary">Tampilkan Widget Download App</span>
            <button
              data-testid="appearance-download-toggle"
              onClick={() => set({ show_download_widget: !form.show_download_widget })}
              className={`relative w-14 h-7 rounded-full transition-colors ${form.show_download_widget ? 'bg-emerald-600' : 'bg-stone-300'}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform ${form.show_download_widget ? 'translate-x-7' : ''}`} />
            </button>
          </div>
          <div>
            <label className="block text-sm font-body font-medium text-text-primary mb-2">Judul Widget Download</label>
            <input
              type="text"
              data-testid="appearance-download-title-input"
              value={form.download_widget_title}
              onChange={(e) => set({ download_widget_title: e.target.value })}
              className={inputCls}
              placeholder="Pasang Aplikasi Masjid"
            />
          </div>
        </div>
      </Section>

      <button
        data-testid="appearance-save-btn"
        onClick={handleSave}
        disabled={saving}
        className="flex items-center gap-2 px-6 py-3 bg-emerald-700 hover:bg-emerald-800 text-white rounded-full font-body font-medium transition-colors disabled:opacity-50"
      >
        <Save className="w-5 h-5" />
        {saving ? 'Menyimpan...' : 'Simpan Pengaturan'}
      </button>
    </motion.div>
  );
};

export default AppearanceManager;
